/**
 * INFINITY LIFE 2.0 — Infinity AI Copilot Component (Google Gemini)
 */

import { store } from '../state.js';
import { api, showToast } from '../api.js';

let isCopilotOpen = false;
let chatMessages = [
  {
    sender: 'ai',
    text: 'Olá! Sou o seu **Copiloto Infinity AI**. Posso analisar suas prioridades de hoje, sugerir o roteiro do seu expediente ou dar aquela bronca construtiva se você estiver enrolando. Como posso ajudar agora?',
    time: 'Agora',
  },
];
let isThinking = false;

export function renderAiCopilot() {
  const { user } = store.get();
  // Não exibir na landing page (somente no ambiente operacional da plataforma)
  const isOwner = user?.is_owner || user?.role === 'owner';
  const userPlan = user?.plan || (isOwner ? 'lifetime' : 'monthly');

  return `
    <!-- Floating AI Trigger Orb -->
    <button class="ai-fab-btn" id="btn-toggle-ai-copilot" title="Abrir Copiloto de IA (Google Gemini)">
      <span class="ai-fab-icon">✨</span>
      <span class="ai-fab-label">Infinity AI</span>
    </button>

    <!-- AI Copilot Drawer / Modal -->
    <div class="ai-drawer-overlay ${isCopilotOpen ? 'open' : ''}" id="ai-drawer-overlay">
      <div class="ai-drawer-card">
        <!-- Header -->
        <div class="ai-drawer-header">
          <div style="display: flex; align-items: center; gap: 10px;">
            <div class="ai-avatar-glow">✨</div>
            <div>
              <div style="font-weight: 800; font-size: 1.05rem; color: var(--text-primary); display: flex; align-items: center; gap: 6px;">
                <span>Infinity AI Copilot</span>
                <span class="badge" style="background: rgba(99, 102, 241, 0.2); color: #818cf8; font-size: 0.68rem; padding: 2px 6px;">Gemini 1.5</span>
              </div>
              <div style="font-size: 0.72rem; color: var(--text-muted);">Orientação de Foco, Rotina & Produtividade</div>
            </div>
          </div>
          <button class="btn-close" id="btn-close-ai-drawer">✕</button>
        </div>

        <!-- Suggestion Action Chips -->
        <div class="ai-chips-scroll">
          <button class="ai-chip-btn" data-prompt="Analisar minhas prioridades de hoje">
            ⚡ Analisar Prioridades Hoje
          </button>
          <button class="ai-chip-btn" data-prompt="Me dê uma bronca construtiva para parar de procrastinar">
            🔥 Bronca Construtiva
          </button>
          <button class="ai-chip-btn" data-prompt="Como organizar minhas entregas comerciais e clientes?">
            💼 Rotina Comercial
          </button>
          <button class="ai-chip-btn" data-prompt="Qual a melhor técnica para foco profundo agora?">
            💡 Foco Profundo
          </button>
        </div>

        <!-- Chat Messages Area -->
        <div class="ai-chat-body" id="ai-chat-messages-container">
          ${chatMessages.map((msg) => renderChatMessage(msg)).join('')}
          ${isThinking ? `
            <div class="ai-msg ai-msg-incoming">
              <div class="ai-typing-indicator">
                <span></span><span></span><span></span>
              </div>
            </div>
          ` : ''}
        </div>

        <!-- Chat Input Footer -->
        <div class="ai-drawer-footer">
          <form id="ai-chat-form" style="display: flex; gap: 8px; width: 100%;">
            <input type="text" id="ai-chat-input" class="form-input" 
                   placeholder="Pergunte ao Gemini ou peça conselhos sobre o dia..." 
                   autocomplete="off" style="border-radius: var(--radius-full); font-size: 0.9rem;" />
            <button type="submit" class="btn-primary" id="btn-send-ai-msg" style="border-radius: var(--radius-full); padding: 0 16px;">
              <span>➤</span>
            </button>
          </form>
        </div>
      </div>
    </div>
  `;
}

function renderChatMessage(msg) {
  const isUser = msg.sender === 'user';
  return `
    <div class="ai-msg ${isUser ? 'ai-msg-outgoing' : 'ai-msg-incoming'}">
      <div class="ai-msg-bubble">
        ${formatAiMarkdown(msg.text)}
      </div>
    </div>
  `;
}

function formatAiMarkdown(text) {
  if (!text) return '';
  return text
    .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
    .replace(/\*(.*?)\*/g, '<em>$1</em>')
    .replace(/\n\n/g, '<br /><br />')
    .replace(/\n/g, '<br />');
}

export function attachAiCopilotEvents(container) {
  const toggleBtn = container.querySelector('#btn-toggle-ai-copilot');
  const closeBtn = container.querySelector('#btn-close-ai-drawer');
  const overlay = container.querySelector('#ai-drawer-overlay');

  const openDrawer = () => {
    isCopilotOpen = true;
    window.dispatchEvent(new CustomEvent('infinity:render'));
    setTimeout(() => {
      const input = document.getElementById('ai-chat-input');
      if (input) input.focus();
    }, 100);
  };

  const closeDrawer = () => {
    isCopilotOpen = false;
    window.dispatchEvent(new CustomEvent('infinity:render'));
  };

  if (toggleBtn) toggleBtn.addEventListener('click', openDrawer);
  if (closeBtn) closeBtn.addEventListener('click', closeDrawer);
  if (overlay) {
    overlay.addEventListener('click', (e) => {
      if (e.target === overlay) closeDrawer();
    });
  }

  // Suggestion Chips
  container.querySelectorAll('.ai-chip-btn').forEach((chip) => {
    chip.addEventListener('click', () => {
      const promptText = chip.dataset.prompt;
      sendMessage(promptText);
    });
  });

  // Form submit
  const form = container.querySelector('#ai-chat-form');
  if (form) {
    form.addEventListener('submit', (e) => {
      e.preventDefault();
      const input = container.querySelector('#ai-chat-input');
      const text = input ? input.value.trim() : '';
      if (text) {
        input.value = '';
        sendMessage(text);
      }
    });
  }

  async function sendMessage(text) {
    chatMessages.push({ sender: 'user', text, time: 'Agora' });
    isThinking = true;
    window.dispatchEvent(new CustomEvent('infinity:render'));

    // Scroll to bottom
    setTimeout(() => {
      const chatBox = document.getElementById('ai-chat-messages-container');
      if (chatBox) chatBox.scrollTop = chatBox.scrollHeight;
    }, 50);

    try {
      const res = await api.aiChat(text);
      isThinking = false;
      if (res.success) {
        chatMessages.push({ sender: 'ai', text: res.reply, time: 'Agora' });
      } else {
        chatMessages.push({ sender: 'ai', text: res.reply || 'Erro ao processar com a IA.', time: 'Agora' });
      }
    } catch (err) {
      isThinking = false;
      chatMessages.push({ sender: 'ai', text: `Desculpe, ocorreu um erro de conexão: ${err.message}`, time: 'Agora' });
    }

    window.dispatchEvent(new CustomEvent('infinity:render'));
    setTimeout(() => {
      const chatBox = document.getElementById('ai-chat-messages-container');
      if (chatBox) chatBox.scrollTop = chatBox.scrollHeight;
    }, 50);
  }
}
